import { useCallback, useEffect, useState } from 'react'
import { parsePhoneNumber } from 'react-phone-number-input'
import { userRepository } from '@/data/users/repositories/user_repository_impl'
import type { UpdateUserPayload, UserDetail } from '@/domain/users/entities/user.entity'
import { GetUserByIdUseCase } from '@/domain/users/usecases/get_user_by_id.usecase'
import { UpdateUserUseCase } from '@/domain/users/usecases/update_user.usecase'
import { toAppError } from '@/core/errors/app_error'
import { logError } from '@/utils/logger'
import type { EditUserFormData } from '../models/edit_user_form'

const defaultGetUserById = new GetUserByIdUseCase(userRepository)
const defaultUpdateUser = new UpdateUserUseCase(userRepository)

export function buildPhoneValue(user: UserDetail): string {
  if (user.countryCode && user.phoneNo) {
    return `${user.countryCode}${user.phoneNo}`
  }
  if (user.phoneNo) {
    return user.phoneNo.startsWith('+') ? user.phoneNo : `+1${user.phoneNo}`
  }
  return ''
}

export function mapUserToFormDefaults(user: UserDetail): EditUserFormData {
  return {
    name: user.name || '',
    email: user.email || '',
    phone: buildPhoneValue(user),
    bio: user.bio || '',
    gender: (user.gender as EditUserFormData['gender']) || 'male',
    dateOfBirth: user.dateOfBirth || '',
    active: user.status === 'active',
  }
}

function parsePhoneFields(phone: string): { countryCode: string; phoneNo: string } {
  let countryCode = ''
  let phoneNo = ''

  if (phone) {
    try {
      const phoneNumber = parsePhoneNumber(phone)
      if (phoneNumber) {
        const callingCode = phoneNumber.countryCallingCode
        const nationalNum = phoneNumber.nationalNumber
        if (callingCode && nationalNum) {
          countryCode = `+${callingCode}`
          phoneNo = nationalNum
        }
      }
    } catch {
      const match = phone.match(/^(\+\d{1,3})(.+)$/)
      if (match?.[1] && match[2]) {
        countryCode = match[1]
        phoneNo = match[2]
      }
    }
  }

  return { countryCode, phoneNo }
}

export function buildUpdatePayload(
  data: EditUserFormData,
  imageUrl?: string
): UpdateUserPayload {
  const { countryCode, phoneNo } = parsePhoneFields(data.phone)
  const payload: UpdateUserPayload = {
    name: data.name,
    email: data.email,
    countryCode,
    phoneNo,
    bio: data.bio,
    gender: data.gender,
    dateOfBirth: data.dateOfBirth,
    status: data.active ? 'active' : 'inactive',
  }
  if (imageUrl) {
    payload.imageUrl = imageUrl
  }
  return payload
}

export interface UseEditUserViewModelDeps {
  getUserById?: GetUserByIdUseCase
  updateUser?: UpdateUserUseCase
}

export function useEditUserViewModel(
  userId: string | undefined,
  deps: UseEditUserViewModelDeps = {}
) {
  const getUserById = deps.getUserById ?? defaultGetUserById
  const updateUser = deps.updateUser ?? defaultUpdateUser

  const [user, setUser] = useState<UserDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!userId) {
      setError('User ID is required')
      setUser(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const detail = await getUserById.execute(userId)
      setUser(detail)
    } catch (err: unknown) {
      logError('Failed to fetch user', err)
      setError(toAppError(err, 'Failed to load user').message)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [getUserById, userId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const submitUpdate = useCallback(
    async (data: EditUserFormData): Promise<{ ok: true } | { ok: false; message: string }> => {
      if (!userId) {
        return { ok: false, message: 'User ID is required' }
      }

      try {
        const payload = buildUpdatePayload(data, user?.imageUrl)
        await updateUser.execute(userId, payload)
        return { ok: true }
      } catch (err: unknown) {
        logError('Failed to update user', err)
        return {
          ok: false,
          message: toAppError(err, 'Failed to update user. Please try again.').message,
        }
      }
    },
    [updateUser, user?.imageUrl, userId]
  )

  return {
    state: { user },
    isLoading,
    error,
    actions: { refresh, submitUpdate },
  }
}
